import Autocomplete from "@material-ui/lab/Autocomplete";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@material-ui/core";
import { CSSProperties, useEffect, useState } from "react";
import { useAppState } from "../components/AppProvider/AppProvider";
import { ConfigurationValue } from "../model";

type EditableFields = "configKey" | "value" | "description";
const TELEGRAM_WEBHOOK_KEY = "telegram_webhook_url";
const actionButtonGroupStyles: CSSProperties = {
  display: "flex",
  justifyContent: "flex-end",
  flexWrap: "wrap",
  gap: 8,
};

const ConfigurationValuesPage = () => {
  const [ctx] = useAppState() ?? [];
  const apiKey = ctx?.apiKey ?? "";
  const baseUrl = ctx?.baseUrl ?? process.env.REACT_APP_BASE_URL ?? "";

  const [configs, setConfigs] = useState<ConfigurationValue[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [savingId, setSavingId] = useState<number | "new" | null>(null);
  const [newConfig, setNewConfig] = useState({
    configKey: "",
    value: "",
    description: "",
  });
  const [availableKeys, setAvailableKeys] = useState<string[]>([]);
  const [availableLoading, setAvailableLoading] = useState<boolean>(false);
  const [infoMessage, setInfoMessage] = useState<string | null>(null);
  const [webhookApplying, setWebhookApplying] = useState<boolean>(false);

  const fetchConfigurationValues = async () => {
    if (!apiKey || !baseUrl) {
      setError("Missing API configuration.");
      return;
    }

    setLoading(true);
    setError(null);
    setInfoMessage(null);
    try {
      const resp = await fetch(`${baseUrl}/configuration-values`, {
        headers: {
          authorization: apiKey,
        },
      });

      if (!resp.ok) {
        throw new Error(await resp.text());
      }

      const data = (await resp.json()) as ConfigurationValue[];
      setConfigs(data);
    } catch (e) {
      console.error(e);
      setError(
        e instanceof Error ? e.message : "Failed to load configuration values."
      );
    } finally {
      setLoading(false);
    }
  };

  const fetchAvailableKeys = async () => {
    if (!apiKey || !baseUrl) {
      return;
    }

    setAvailableLoading(true);
    try {
      const resp = await fetch(`${baseUrl}/configuration-values/available`, {
        headers: {
          authorization: apiKey,
        },
      });

      if (!resp.ok) {
        throw new Error(await resp.text());
      }

      const keys = (await resp.json()) as string[];
      setAvailableKeys(keys ?? []);
    } catch (e) {
      console.error(e);
      setError(
        e instanceof Error ? e.message : "Failed to load available keys."
      );
    } finally {
      setAvailableLoading(false);
    }
  };

  useEffect(() => {
    fetchConfigurationValues();
    fetchAvailableKeys();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiKey, baseUrl]);

  const handleFieldChange = (
    id: number,
    field: EditableFields,
    value: string
  ) => {
    setConfigs((prev) =>
      prev.map((cfg) => (cfg.id === id ? { ...cfg, [field]: value } : cfg))
    );
  };

  const handleSave = async (config: ConfigurationValue): Promise<boolean> => {
    if (!apiKey || !baseUrl) {
      setError("Missing API configuration.");
      return false;
    }

    setSavingId(config.id);
    setError(null);
    setInfoMessage(null);

    try {
      const resp = await fetch(`${baseUrl}/configuration-values/${config.id}`, {
        method: "PUT",
        headers: {
          "content-type": "application/json",
          authorization: apiKey,
        },
        body: JSON.stringify({
          configKey: config.configKey,
          value: config.value,
          description: config.description,
        }),
      });

      if (!resp.ok) {
        throw new Error(await resp.text());
      }

      const updated = (await resp.json()) as ConfigurationValue;
      setConfigs((prev) =>
        prev.map((cfg) => (cfg.id === updated.id ? updated : cfg))
      );
      return true;
    } catch (e) {
      console.error(e);
      setError(
        e instanceof Error ? e.message : "Failed to save configuration value."
      );
      return false;
    } finally {
      setSavingId(null);
    }
  };

  const handleCreate = async () => {
    if (!apiKey || !baseUrl) return;

    if (!newConfig.configKey.trim()) {
      setError("Configuration key is required.");
      return;
    }

    setSavingId("new");
    setError(null);
    setInfoMessage(null);

    try {
      const resp = await fetch(`${baseUrl}/configuration-values`, {
        method: "POST",
        headers: {
          "content-type": "application/json",
          authorization: apiKey,
        },
        body: JSON.stringify({
          configKey: newConfig.configKey.trim(),
          value: newConfig.value,
          description: newConfig.description,
        }),
      });

      if (!resp.ok) {
        throw new Error(await resp.text());
      }

      setNewConfig({ configKey: "", value: "", description: "" });
      await fetchConfigurationValues();
    } catch (e) {
      console.error(e);
      setError(
        e instanceof Error ? e.message : "Failed to create configuration value."
      );
    } finally {
      setSavingId(null);
    }
  };

  const handleApplyTelegramWebhook = async (config: ConfigurationValue) => {
    if (!apiKey || !baseUrl) {
      setError("Missing API configuration.");
      return;
    }

    const trimmedValue = config.value?.trim();
    if (!trimmedValue) {
      setError("Telegram webhook URL is required before applying.");
      return;
    }

    setInfoMessage(null);
    setError(null);
    setWebhookApplying(true);

    try {
      const saved = await handleSave(config);
      if (!saved) {
        return;
      }

      const resp = await fetch(
        `${baseUrl}/configuration-values/telegram-webhook`,
        {
          method: "POST",
          headers: {
            authorization: apiKey,
          },
        }
      );

      if (!resp.ok) {
        throw new Error(await resp.text());
      }

      setInfoMessage("Telegram webhook applied successfully.");
    } catch (e) {
      console.error(e);
      setError(
        e instanceof Error
          ? e.message
          : "Failed to apply Telegram webhook."
      );
    } finally {
      setWebhookApplying(false);
    }
  };

  const handleRefresh = () => {
    fetchConfigurationValues();
    fetchAvailableKeys();
  };

  return (
    <Box p={3}>
      <Box mb={2} display="flex" justifyContent="space-between">
        <div>
          <Typography variant="h5">Configuration Values</Typography>
          <Typography variant="body2" color="textSecondary">
            Inspect and update system configuration entries such as API tokens
            and service URLs.
          </Typography>
        </div>
        <Button
          variant="outlined"
          color="primary"
          onClick={handleRefresh}
          disabled={loading || availableLoading || webhookApplying}
        >
          Refresh
        </Button>
      </Box>

      {error && (
        <Box mb={2}>
          <Typography color="error">{error}</Typography>
        </Box>
      )}
      {infoMessage && (
        <Box mb={2}>
          <Typography color="primary">{infoMessage}</Typography>
        </Box>
      )}

      <Card>
        <CardContent>
          {loading ? (
            <Box display="flex" justifyContent="center" my={4}>
              <CircularProgress />
            </Box>
          ) : (
            <Paper>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell style={{ fontWeight: 600 }}>Key</TableCell>
                    <TableCell style={{ fontWeight: 600 }}>Value</TableCell>
                    <TableCell style={{ fontWeight: 600 }}>
                      Description
                    </TableCell>
                    <TableCell style={{ width: 140 }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {configs.map((config) => {
                    const normalizedKey = (config.configKey ?? "")
                      .trim()
                      .toLowerCase();
                    const showApplyWebhookButton =
                      normalizedKey === TELEGRAM_WEBHOOK_KEY &&
                      Boolean(config.value?.trim());

                    return (
                      <TableRow key={config.id}>
                        <TableCell>
                          <Autocomplete
                            fullWidth
                            freeSolo
                            options={availableKeys}
                            loading={availableLoading}
                            value={config.configKey ?? ""}
                            inputValue={config.configKey ?? ""}
                            onChange={(_, newValue) =>
                              handleFieldChange(
                                config.id,
                                "configKey",
                                newValue ?? ""
                              )
                            }
                            onInputChange={(_, newValue) =>
                              handleFieldChange(
                                config.id,
                                "configKey",
                                newValue ?? ""
                              )
                            }
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                placeholder="Config key"
                                label="Key"
                              />
                            )}
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            fullWidth
                            value={config.value ?? ""}
                            placeholder="Config value"
                            onChange={(e) =>
                              handleFieldChange(
                                config.id,
                                "value",
                                e.target.value
                              )
                            }
                          />
                        </TableCell>
                        <TableCell>
                          <TextField
                            fullWidth
                            value={config.description ?? ""}
                            placeholder="Description"
                            onChange={(e) =>
                              handleFieldChange(
                                config.id,
                                "description",
                                e.target.value
                              )
                            }
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Box style={actionButtonGroupStyles}>
                            {showApplyWebhookButton && (
                              <Button
                                variant="outlined"
                                color="default"
                                size="small"
                                onClick={() =>
                                  handleApplyTelegramWebhook(config)
                                }
                                disabled={
                                  webhookApplying || savingId === config.id
                                }
                              >
                                {webhookApplying
                                  ? "Applying..."
                                  : "Set webhook"}
                              </Button>
                            )}
                            <Button
                              variant="contained"
                              color="primary"
                              size="small"
                              onClick={() => handleSave(config)}
                              disabled={savingId === config.id}
                            >
                              {savingId === config.id ? "Saving..." : "Save"}
                            </Button>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}

                  <TableRow>
                    <TableCell>
                      <Autocomplete
                        fullWidth
                        freeSolo
                        options={availableKeys}
                        loading={availableLoading}
                        value={newConfig.configKey}
                        inputValue={newConfig.configKey}
                        onChange={(_, newValue) =>
                          setNewConfig((prev) => ({
                            ...prev,
                            configKey: newValue ?? "",
                          }))
                        }
                        onInputChange={(_, newValue) =>
                          setNewConfig((prev) => ({
                            ...prev,
                            configKey: newValue ?? "",
                          }))
                        }
                        renderInput={(params) => (
                          <TextField {...params} label="New key" />
                        )}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        fullWidth
                        label="New value"
                        value={newConfig.value}
                        onChange={(e) =>
                          setNewConfig((prev) => ({
                            ...prev,
                            value: e.target.value,
                          }))
                        }
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        fullWidth
                        label="Description"
                        value={newConfig.description}
                        onChange={(e) =>
                          setNewConfig((prev) => ({
                            ...prev,
                            description: e.target.value,
                          }))
                        }
                      />
                    </TableCell>
                    <TableCell align="right">
                      <Box style={actionButtonGroupStyles}>
                        <Button
                          variant="contained"
                          color="secondary"
                          size="small"
                          onClick={handleCreate}
                          disabled={savingId === "new"}
                        >
                          {savingId === "new" ? "Saving..." : "Add"}
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </Paper>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default ConfigurationValuesPage;
